// Geometry, drawing and hit-testing for the video-session whiteboard.
//
// The board is a display list of items repainted from scratch on every
// change (see Whiteboard.tsx). Everything here is pure canvas work in the
// board's own fixed coordinate space — nothing is persisted or transmitted
// as data; the canvas is captured as a video track, so the client only ever
// receives pixels.

export const WHITEBOARD_WIDTH = 1600
export const WHITEBOARD_HEIGHT = 1000

export const BACKGROUND = "#ffffff"

// Marker ink and fill colors — content on the board, not UI chrome, so they
// are deliberate literals rather than design-system tokens.
export const INKS = [
  { name: "Black", value: "#1f2937" },
  { name: "Red", value: "#dc2626" },
  { name: "Blue", value: "#2563eb" },
  { name: "Green", value: "#16a34a" },
]

export const FILLS = [
  { name: "No fill", value: null },
  { name: "White", value: "#ffffff" },
  { name: "Red", value: "#fee2e2" },
  { name: "Blue", value: "#dbeafe" },
  { name: "Green", value: "#dcfce7" },
  { name: "Yellow", value: "#fef9c3" },
]

export const PEN_WIDTH = 5
export const LINE_WIDTH = 4
export const SHAPE_WIDTH = 4
// Text metrics live in canvas units. The font stack matches --font-sans so
// the canvas text and the overlaid textarea lay out identically.
export const TEXT_SIZE = 44
export const LABEL_SIZE = 30
export const TEXT_LINE_HEIGHT = 1.3
export const TEXT_FONT = "'Inter', ui-sans-serif, system-ui, sans-serif"
// Selection chrome is drawn on the canvas, so it is visible to the client
// too — that is deliberate: the therapist pointing at a box is part of the
// conversation.
export const SELECTION = "#0d9488"
export const HANDLE = 9

export type Point = { x: number; y: number }

export type Align = "left" | "center" | "right"

// Where a run of text starts, given the anchor it is aligned around.
export function alignedLeft(x: number, width: number, align: Align): number {
  if (align === "center") return x - width / 2
  if (align === "right") return x - width
  return x
}

export type ShapeKind = "rect" | "round" | "ellipse" | "diamond" | "triangle"
export type ArrowEnds = "none" | "end" | "both"

export type Stroke = {
  kind: "stroke"
  id: string
  color: string
  width: number
  points: Point[]
}

export type TextBlock = {
  kind: "text"
  id: string
  color: string
  size: number
  // x is the left edge for "left" text and the centre line for "center",
  // which is what annotations placed around a diagram want.
  x: number
  y: number
  align: Align
  text: string
}

export type Shape = {
  kind: "shape"
  id: string
  shape: ShapeKind
  x: number
  y: number
  w: number
  h: number
  color: string
  fill: string | null
  size: number
  text: string
}

export type Connector = {
  kind: "connector"
  id: string
  color: string
  from: Point
  to: Point
  // When bound to a shape the endpoint is recomputed against that shape's
  // border, so moving a box drags its arrows with it.
  fromId?: string
  toId?: string
  arrow: ArrowEnds
  // Perpendicular offset of the curve's control point at the midpoint;
  // 0 draws a straight line.
  bow: number
  dashed?: boolean
}

export type PieSlice = { text: string; value: number; color: string }

export type Pie = {
  kind: "pie"
  id: string
  x: number
  y: number
  r: number
  color: string
  slices: PieSlice[]
}

export type Item = Stroke | TextBlock | Shape | Connector | Pie

export type Bounds = { x: number; y: number; w: number; h: number }

// Ids are only ever minted from event handlers, never during render, so a
// plain counter keeps them deterministic and SSR-safe.
let seq = 0
export function nextId(): string {
  seq += 1
  return `i${seq}`
}

// Undo keeps whole-board snapshots, so every mutable part has to be copied
// out of the live display list.
export function cloneItems(items: Item[]): Item[] {
  return items.map((item) => {
    const next = { ...item }
    if (next.kind === "stroke") next.points = next.points.map((p) => ({ ...p }))
    if (next.kind === "connector") {
      next.from = { ...next.from }
      next.to = { ...next.to }
    }
    if (next.kind === "pie") next.slices = next.slices.map((slice) => ({ ...slice }))
    return next
  })
}

export function byId(items: Item[]): Map<string, Item> {
  return new Map(items.map((item) => [item.id, item]))
}

export function textFont(size: number): string {
  return `${size}px ${TEXT_FONT}`
}

// Canvas "top" baseline is the top of the em box; the textarea puts half the
// leading above it. Shifting by that keeps the two in register.
export function topOffset(size: number): number {
  return (size * (TEXT_LINE_HEIGHT - 1)) / 2
}

export function lineStep(size: number): number {
  return size * TEXT_LINE_HEIGHT
}

// Splits on explicit newlines, then wraps each line to maxWidth. A word too
// long to fit is left overflowing rather than broken mid-word.
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  size: number,
  maxWidth?: number,
): string[] {
  const paragraphs = text.split("\n")
  if (!maxWidth || maxWidth <= 0) return paragraphs
  ctx.font = textFont(size)
  const out: string[] = []
  for (const paragraph of paragraphs) {
    const words = paragraph.split(" ")
    let line = ""
    for (const word of words) {
      const candidate = line === "" ? word : `${line} ${word}`
      if (line !== "" && ctx.measureText(candidate).width > maxWidth) {
        out.push(line)
        line = word
      } else {
        line = candidate
      }
    }
    out.push(line)
  }
  return out
}

export function measureLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  size: number,
): { w: number; h: number } {
  ctx.font = textFont(size)
  const w = Math.max(0, ...lines.map((line) => ctx.measureText(line).width))
  return { w, h: Math.max(1, lines.length) * lineStep(size) }
}

function drawLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  x: number,
  y: number,
  size: number,
  color: string,
  align: CanvasTextAlign,
) {
  ctx.fillStyle = color
  ctx.font = textFont(size)
  ctx.textAlign = align
  ctx.textBaseline = "top"
  lines.forEach((line, i) => {
    ctx.fillText(line, x, y + topOffset(size) + i * lineStep(size))
  })
  ctx.textAlign = "left"
}

export function drawStroke(ctx: CanvasRenderingContext2D, stroke: Stroke) {
  if (stroke.points.length === 0) return
  ctx.strokeStyle = stroke.color
  ctx.lineWidth = stroke.width
  ctx.lineCap = "round"
  ctx.lineJoin = "round"
  ctx.beginPath()
  ctx.moveTo(stroke.points[0].x, stroke.points[0].y)
  for (const p of stroke.points.slice(1)) ctx.lineTo(p.x, p.y)
  ctx.stroke()
}

export function drawText(ctx: CanvasRenderingContext2D, block: TextBlock, override?: string) {
  const lines = (override ?? block.text).split("\n")
  drawLines(ctx, lines, block.x, block.y, block.size, block.color, block.align)
}

// Rounded rectangle by hand rather than ctx.roundRect: the board has to work
// on the older Safari versions this app deliberately still serves.
function roundRectPath(ctx: CanvasRenderingContext2D, b: Bounds, radius: number) {
  const r = Math.max(0, Math.min(radius, b.w / 2, b.h / 2))
  ctx.moveTo(b.x + r, b.y)
  ctx.lineTo(b.x + b.w - r, b.y)
  ctx.quadraticCurveTo(b.x + b.w, b.y, b.x + b.w, b.y + r)
  ctx.lineTo(b.x + b.w, b.y + b.h - r)
  ctx.quadraticCurveTo(b.x + b.w, b.y + b.h, b.x + b.w - r, b.y + b.h)
  ctx.lineTo(b.x + r, b.y + b.h)
  ctx.quadraticCurveTo(b.x, b.y + b.h, b.x, b.y + b.h - r)
  ctx.lineTo(b.x, b.y + r)
  ctx.quadraticCurveTo(b.x, b.y, b.x + r, b.y)
  ctx.closePath()
}

// Builds the shape's outline as the current path, ready to fill, stroke or
// hit-test with isPointInPath. The context is never transformed, so path
// coordinates are board coordinates.
export function shapePath(ctx: CanvasRenderingContext2D, s: Shape) {
  const { x, y, w, h } = s
  ctx.beginPath()
  switch (s.shape) {
    case "rect":
      ctx.rect(x, y, w, h)
      break
    case "round":
      roundRectPath(ctx, s, Math.min(w, h) * 0.18)
      break
    case "ellipse":
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
      break
    case "diamond":
      ctx.moveTo(x + w / 2, y)
      ctx.lineTo(x + w, y + h / 2)
      ctx.lineTo(x + w / 2, y + h)
      ctx.lineTo(x, y + h / 2)
      ctx.closePath()
      break
    case "triangle":
      ctx.moveTo(x + w / 2, y)
      ctx.lineTo(x + w, y + h)
      ctx.lineTo(x, y + h)
      ctx.closePath()
      break
  }
}

// Where a shape's label sits and how wide it may run. The editing overlay
// reads the same numbers, so text stays in register while it is being typed.
export function labelBox(s: Shape): { width: number; cy: number } {
  const padding = s.shape === "ellipse" || s.shape === "diamond" ? s.w * 0.16 : 18
  // Triangles taper, so their label sits in the lower half where there is
  // room for it; every other shape centres.
  const cy = s.shape === "triangle" ? s.y + s.h * 0.62 : s.y + s.h / 2
  return { width: Math.max(s.size, s.w - padding * 2), cy }
}

export function drawShape(ctx: CanvasRenderingContext2D, s: Shape, override?: string) {
  shapePath(ctx, s)
  if (s.fill) {
    ctx.fillStyle = s.fill
    ctx.fill()
  }
  ctx.strokeStyle = s.color
  ctx.lineWidth = SHAPE_WIDTH
  ctx.lineJoin = "round"
  ctx.stroke()

  const text = override ?? s.text
  if (text === "") return
  const { width, cy } = labelBox(s)
  const lines = wrapText(ctx, text, s.size, width)
  const { h } = measureLines(ctx, lines, s.size)
  drawLines(ctx, lines, s.x + s.w / 2, cy - h / 2, s.size, s.color, "center")
}

function quadPoint(p0: Point, c: Point, p1: Point, t: number): Point {
  const u = 1 - t
  return {
    x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x,
    y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y,
  }
}

// Where a ray from the shape's centre towards `target` leaves the shape.
// Rect-family shapes use the slab clip; ellipses use the ellipse formula;
// diamonds use their own L1 metric. Triangles borrow the rectangle, which
// overlaps slightly and is not worth more code.
export function borderPoint(s: Shape, target: Point): Point {
  const cx = s.x + s.w / 2
  const cy = s.y + s.h / 2
  const dx = target.x - cx
  const dy = target.y - cy
  if (dx === 0 && dy === 0) return { x: cx, y: cy }
  const hw = s.w / 2
  const hh = s.h / 2
  let t: number
  if (s.shape === "ellipse") {
    t = 1 / Math.sqrt((dx / hw) ** 2 + (dy / hh) ** 2)
  } else if (s.shape === "diamond") {
    t = 1 / (Math.abs(dx) / hw + Math.abs(dy) / hh)
  } else {
    t = Math.min(hw / Math.abs(dx) || Infinity, hh / Math.abs(dy) || Infinity)
  }
  return { x: cx + dx * t, y: cy + dy * t }
}

// Resolves a connector's drawn endpoints, pulling bound ends back to the
// border of the shape they attach to.
export function connectorEnds(c: Connector, lookup: Map<string, Item>): { from: Point; to: Point } {
  const fromShape = c.fromId ? lookup.get(c.fromId) : undefined
  const toShape = c.toId ? lookup.get(c.toId) : undefined
  const fromAnchor =
    fromShape?.kind === "shape"
      ? { x: fromShape.x + fromShape.w / 2, y: fromShape.y + fromShape.h / 2 }
      : c.from
  const toAnchor =
    toShape?.kind === "shape"
      ? { x: toShape.x + toShape.w / 2, y: toShape.y + toShape.h / 2 }
      : c.to
  return {
    from: fromShape?.kind === "shape" ? borderPoint(fromShape, toAnchor) : fromAnchor,
    to: toShape?.kind === "shape" ? borderPoint(toShape, fromAnchor) : toAnchor,
  }
}

function controlPoint(from: Point, to: Point, bow: number): Point {
  const mx = (from.x + to.x) / 2
  const my = (from.y + to.y) / 2
  if (bow === 0) return { x: mx, y: my }
  const dx = to.x - from.x
  const dy = to.y - from.y
  const len = Math.hypot(dx, dy) || 1
  // The control point is twice the desired bow because a quadratic curve
  // reaches only halfway to it at t = 0.5.
  return { x: mx + (-dy / len) * bow * 2, y: my + (dx / len) * bow * 2 }
}

function arrowHead(ctx: CanvasRenderingContext2D, tip: Point, from: Point, color: string) {
  const angle = Math.atan2(tip.y - from.y, tip.x - from.x)
  const size = 20
  const spread = 0.42
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(tip.x, tip.y)
  ctx.lineTo(tip.x - size * Math.cos(angle - spread), tip.y - size * Math.sin(angle - spread))
  ctx.lineTo(tip.x - size * Math.cos(angle + spread), tip.y - size * Math.sin(angle + spread))
  ctx.closePath()
  ctx.fill()
}

export function drawConnector(ctx: CanvasRenderingContext2D, c: Connector, lookup: Map<string, Item>) {
  const { from, to } = connectorEnds(c, lookup)
  const control = controlPoint(from, to, c.bow)
  ctx.strokeStyle = c.color
  ctx.lineWidth = LINE_WIDTH
  ctx.lineCap = "round"
  ctx.setLineDash(c.dashed ? [14, 10] : [])
  ctx.beginPath()
  ctx.moveTo(from.x, from.y)
  ctx.quadraticCurveTo(control.x, control.y, to.x, to.y)
  ctx.stroke()
  ctx.setLineDash([])
  if (c.arrow === "end" || c.arrow === "both") arrowHead(ctx, to, quadPoint(from, control, to, 0.9), c.color)
  if (c.arrow === "both") arrowHead(ctx, from, quadPoint(from, control, to, 0.1), c.color)
}

// Slice boundaries in radians, starting at 12 o'clock and running clockwise
// the way a pie chart is read.
export function pieAngles(pie: Pie): { start: number; end: number; mid: number }[] {
  const total = pie.slices.reduce((sum, s) => sum + Math.max(0, s.value), 0) || 1
  let angle = -Math.PI / 2
  return pie.slices.map((slice) => {
    const sweep = (Math.max(0, slice.value) / total) * Math.PI * 2
    const out = { start: angle, end: angle + sweep, mid: angle + sweep / 2 }
    angle += sweep
    return out
  })
}

export const PIE_LABEL_WIDTH = 260

export function pieLabelAnchor(
  pie: Pie,
  index: number,
): { x: number; y: number; align: Align; size: number } {
  const mid = pieAngles(pie)[index].mid
  const cos = Math.cos(mid)
  return {
    x: pie.x + cos * (pie.r + 34),
    y: pie.y + Math.sin(mid) * (pie.r + 34),
    // Labels read outwards from the circle, so the ones on the left hang off
    // to the left instead of overlapping the slices.
    align: cos > 0.2 ? "left" : cos < -0.2 ? "right" : "center",
    size: LABEL_SIZE - 2,
  }
}

// The slice a point falls in, for double-click-to-relabel.
export function pieSliceAt(pie: Pie, p: Point): number | null {
  if (Math.hypot(p.x - pie.x, p.y - pie.y) > pie.r + 40) return null
  const start = -Math.PI / 2
  let angle = Math.atan2(p.y - pie.y, p.x - pie.x)
  while (angle < start) angle += Math.PI * 2
  const angles = pieAngles(pie)
  for (let i = 0; i < angles.length; i++) {
    if (angle >= angles[i].start && angle <= angles[i].end) return i
  }
  return null
}

export function drawPie(ctx: CanvasRenderingContext2D, pie: Pie, overrideIndex?: number, override?: string) {
  const total = pie.slices.reduce((sum, s) => sum + Math.max(0, s.value), 0) || 1
  const angles = pieAngles(pie)
  angles.forEach((a, i) => {
    ctx.beginPath()
    ctx.moveTo(pie.x, pie.y)
    ctx.arc(pie.x, pie.y, pie.r, a.start, a.end)
    ctx.closePath()
    ctx.fillStyle = pie.slices[i].color
    ctx.fill()
    ctx.strokeStyle = pie.color
    ctx.lineWidth = SHAPE_WIDTH
    ctx.lineJoin = "round"
    ctx.stroke()
  })
  ctx.beginPath()
  ctx.arc(pie.x, pie.y, pie.r, 0, Math.PI * 2)
  ctx.strokeStyle = pie.color
  ctx.lineWidth = SHAPE_WIDTH
  ctx.stroke()

  // Labels sit outside the circle so a thin slice stays readable; the
  // percentage goes inside it.
  angles.forEach((a, i) => {
    const slice = pie.slices[i]
    const text = i === overrideIndex ? (override ?? slice.text) : slice.text
    const percent = Math.round((Math.max(0, slice.value) / total) * 100)
    const size = LABEL_SIZE - 2
    if (percent >= 7) {
      const px = pie.x + Math.cos(a.mid) * pie.r * 0.62
      const py = pie.y + Math.sin(a.mid) * pie.r * 0.62
      drawLines(ctx, [`${percent}%`], px, py - lineStep(size) / 2, size, pie.color, "center")
    }
    if (text === "") return
    const anchor = pieLabelAnchor(pie, i)
    const lines = wrapText(ctx, text, anchor.size, PIE_LABEL_WIDTH)
    const { h } = measureLines(ctx, lines, anchor.size)
    drawLines(ctx, lines, anchor.x, anchor.y - h / 2, anchor.size, pie.color, anchor.align)
  })
}

export function drawItem(
  ctx: CanvasRenderingContext2D,
  item: Item,
  lookup: Map<string, Item>,
  edit?: { id: string; value: string; sliceIndex?: number },
) {
  const override = edit && edit.id === item.id ? edit.value : undefined
  switch (item.kind) {
    case "stroke":
      drawStroke(ctx, item)
      break
    case "text":
      drawText(ctx, item, override)
      break
    case "shape":
      drawShape(ctx, item, override)
      break
    case "connector":
      drawConnector(ctx, item, lookup)
      break
    case "pie":
      drawPie(ctx, item, override === undefined ? undefined : edit?.sliceIndex, override)
      break
  }
}

export function boundsOf(ctx: CanvasRenderingContext2D, item: Item, lookup: Map<string, Item>): Bounds {
  switch (item.kind) {
    case "shape":
      return { x: item.x, y: item.y, w: item.w, h: item.h }
    case "pie":
      return { x: item.x - item.r, y: item.y - item.r, w: item.r * 2, h: item.r * 2 }
    case "text": {
      const lines = item.text.split("\n")
      const { w, h } = measureLines(ctx, lines, item.size)
      const width = Math.max(w, item.size)
      return { x: alignedLeft(item.x, width, item.align), y: item.y, w: width, h }
    }
    case "connector": {
      const { from, to } = connectorEnds(item, lookup)
      return {
        x: Math.min(from.x, to.x),
        y: Math.min(from.y, to.y),
        w: Math.abs(to.x - from.x),
        h: Math.abs(to.y - from.y),
      }
    }
    case "stroke": {
      const xs = item.points.map((p) => p.x)
      const ys = item.points.map((p) => p.y)
      const x = Math.min(...xs)
      const y = Math.min(...ys)
      return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y }
    }
  }
}

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSquared = dx * dx + dy * dy
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

const TOUCH_SLOP = 12

function hits(ctx: CanvasRenderingContext2D, item: Item, p: Point, lookup: Map<string, Item>): boolean {
  switch (item.kind) {
    case "stroke": {
      const reach = item.width / 2 + TOUCH_SLOP
      if (item.points.length === 1) return Math.hypot(p.x - item.points[0].x, p.y - item.points[0].y) <= reach
      for (let i = 1; i < item.points.length; i++) {
        if (distanceToSegment(p, item.points[i - 1], item.points[i]) <= reach) return true
      }
      return false
    }
    case "shape": {
      shapePath(ctx, item)
      if (ctx.isPointInPath(p.x, p.y)) return true
      ctx.lineWidth = SHAPE_WIDTH + TOUCH_SLOP * 2
      return ctx.isPointInStroke(p.x, p.y)
    }
    case "pie":
      return Math.hypot(p.x - item.x, p.y - item.y) <= item.r + TOUCH_SLOP
    case "text": {
      const b = boundsOf(ctx, item, lookup)
      return p.x >= b.x - TOUCH_SLOP && p.x <= b.x + b.w + TOUCH_SLOP && p.y >= b.y - TOUCH_SLOP && p.y <= b.y + b.h + TOUCH_SLOP
    }
    case "connector": {
      const { from, to } = connectorEnds(item, lookup)
      const control = controlPoint(from, to, item.bow)
      let previous = from
      for (let i = 1; i <= 16; i++) {
        const next = quadPoint(from, control, to, i / 16)
        if (distanceToSegment(p, previous, next) <= LINE_WIDTH / 2 + TOUCH_SLOP) return true
        previous = next
      }
      return false
    }
  }
}

// Topmost item under the point, matching what the eye expects: later items
// are painted over earlier ones, so they are tested first.
export function hitTest(ctx: CanvasRenderingContext2D, items: Item[], p: Point): Item | null {
  const lookup = byId(items)
  for (let i = items.length - 1; i >= 0; i--) {
    if (hits(ctx, items[i], p, lookup)) return items[i]
  }
  return null
}

export function shapeAt(ctx: CanvasRenderingContext2D, items: Item[], p: Point): Shape | null {
  const lookup = byId(items)
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]
    if (item.kind === "shape" && hits(ctx, item, p, lookup)) return item
  }
  return null
}

export type Handle = { id: string; x: number; y: number }

const MIN_SIZE = 48

export function handlesFor(ctx: CanvasRenderingContext2D, item: Item, lookup: Map<string, Item>): Handle[] {
  if (item.kind === "connector") {
    const { from, to } = connectorEnds(item, lookup)
    return [
      { id: "from", ...from },
      { id: "to", ...to },
    ]
  }
  if (item.kind === "pie") {
    // Boundary handles re-apportion the slices; the corner handles resize
    // the whole chart.
    const boundaries = pieAngles(item).map((a, i) => ({
      id: `slice${i}`,
      x: item.x + Math.cos(a.start) * item.r,
      y: item.y + Math.sin(a.start) * item.r,
    }))
    return [...boundaries, ...cornerHandles(boundsOf(ctx, item, lookup))]
  }
  if (item.kind === "shape") return cornerHandles(boundsOf(ctx, item, lookup))
  return []
}

function cornerHandles(b: Bounds): Handle[] {
  return [
    { id: "nw", x: b.x, y: b.y },
    { id: "ne", x: b.x + b.w, y: b.y },
    { id: "se", x: b.x + b.w, y: b.y + b.h },
    { id: "sw", x: b.x, y: b.y + b.h },
  ]
}

export function handleAt(handles: Handle[], p: Point): Handle | null {
  for (const handle of handles) {
    if (Math.abs(p.x - handle.x) <= HANDLE + 6 && Math.abs(p.y - handle.y) <= HANDLE + 6) return handle
  }
  return null
}

// Applies a handle drag. Connector endpoint rebinding is left to the caller,
// which is the only place that knows the full item list.
export function dragHandle(item: Item, handleId: string, p: Point) {
  if (item.kind === "connector") {
    if (handleId === "from") {
      item.from = p
      item.fromId = undefined
    } else {
      item.to = p
      item.toId = undefined
    }
    return
  }
  if (item.kind === "pie" && handleId.startsWith("slice")) {
    reapportion(item, Number(handleId.slice(5)), p)
    return
  }
  if (item.kind === "pie") {
    const b = resizeBounds({ x: item.x - item.r, y: item.y - item.r, w: item.r * 2, h: item.r * 2 }, handleId, p)
    item.r = Math.max(MIN_SIZE, Math.min(b.w, b.h) / 2)
    item.x = b.x + b.w / 2
    item.y = b.y + b.h / 2
    return
  }
  if (item.kind === "shape") {
    const b = resizeBounds(item, handleId, p)
    item.x = b.x
    item.y = b.y
    item.w = b.w
    item.h = b.h
  }
}

function resizeBounds(b: Bounds, handleId: string, p: Point): Bounds {
  const left = handleId === "nw" || handleId === "sw" ? p.x : b.x
  const top = handleId === "nw" || handleId === "ne" ? p.y : b.y
  const right = handleId === "ne" || handleId === "se" ? p.x : b.x + b.w
  const bottom = handleId === "se" || handleId === "sw" ? p.y : b.y + b.h
  const x = Math.min(left, right - MIN_SIZE)
  const y = Math.min(top, bottom - MIN_SIZE)
  return { x, y, w: Math.max(MIN_SIZE, right - x), h: Math.max(MIN_SIZE, bottom - y) }
}

// Dragging the boundary between slice i-1 and slice i trades value between
// exactly those two, so the rest of the chart holds still.
function reapportion(pie: Pie, index: number, p: Point) {
  const count = pie.slices.length
  if (count < 2) return
  const previous = (index - 1 + count) % count
  const angles = pieAngles(pie)
  const total = pie.slices.reduce((sum, s) => sum + Math.max(0, s.value), 0) || 1
  const perRadian = total / (Math.PI * 2)
  const start = angles[previous].start
  let target = Math.atan2(p.y - pie.y, p.x - pie.x)
  // Unwrap the pointer angle into the pair's own span so the boundary can't
  // jump to the far side of the circle.
  while (target < start) target += Math.PI * 2
  const span = angles[previous].end - angles[previous].start + (angles[index].end - angles[index].start)
  const minimum = 0.04
  const first = Math.max(minimum, Math.min(span - minimum, target - start))
  pie.slices[previous].value = first * perRadian
  pie.slices[index].value = (span - first) * perRadian
}

export function drawSelection(ctx: CanvasRenderingContext2D, item: Item, lookup: Map<string, Item>) {
  const b = boundsOf(ctx, item, lookup)
  ctx.strokeStyle = SELECTION
  ctx.lineWidth = 2
  ctx.setLineDash([10, 8])
  ctx.strokeRect(b.x - 10, b.y - 10, b.w + 20, b.h + 20)
  ctx.setLineDash([])
  for (const handle of handlesFor(ctx, item, lookup)) {
    ctx.fillStyle = BACKGROUND
    ctx.fillRect(handle.x - HANDLE, handle.y - HANDLE, HANDLE * 2, HANDLE * 2)
    ctx.strokeStyle = SELECTION
    ctx.lineWidth = 3
    ctx.strokeRect(handle.x - HANDLE, handle.y - HANDLE, HANDLE * 2, HANDLE * 2)
  }
}
